// components/ProtectedRoute.jsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Alert, Button, Stack, Typography } from '@mui/material';
import { Home as HomeIcon, Dashboard as DashboardIcon } from '@mui/icons-material';

const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false }) => {
  const { currentUser, isAdmin, isSuperAdmin, loading, isApproved, userData } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not logged in
  if (!currentUser) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // Email not verified
  if (!currentUser.emailVerified) {
    return <Navigate to="/auth/verify-email" replace />;
  }

  // Check if user has incomplete application
  // This allows access to complete-application page only
  if (userData && (userData.applicationStatus === 'incomplete' || userData.role === 'pending_application')) {
    // If trying to access complete-application page, allow it
    if (window.location.pathname === '/auth/complete-application') {
      return children;
    }
    // Otherwise redirect to complete application
    return <Navigate to="/auth/complete-application" replace />;
  }

  // Check for pending approval (application submitted but not yet approved)
  if (userData && userData.applicationStatus === 'pending_review' && !isApproved) {
    // Allow access to pending page
    if (window.location.pathname === '/auth/pending-approval') {
      return children;
    }
    return <Navigate to="/auth/pending-approval" replace />;
  }

  // Check for super admin requirement
  if (requireSuperAdmin && !isSuperAdmin) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Access Denied</Typography>
          <Typography variant="body2">
            This area requires Super Administrator privileges. Please contact your system administrator.
          </Typography>
        </Alert>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button 
            variant="contained" 
            onClick={() => window.location.href = '/admin/dashboard'}
            startIcon={<DashboardIcon />}
          >
            Go to Dashboard
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => window.location.href = '/'}
            startIcon={<HomeIcon />}
          >
            Home
          </Button>
        </Stack>
      </Box>
    );
  }

  // Check for admin requirement (includes super admin)
  if (requireAdmin && !isAdmin) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Access Denied</Typography>
          <Typography variant="body2">
            You don't have permission to access this page. This area is restricted to administrators only.
          </Typography>
        </Alert>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button 
            variant="outlined" 
            onClick={() => window.location.href = '/'}
            startIcon={<HomeIcon />}
          >
            Return to Home
          </Button>
        </Stack>
      </Box>
    );
  }

  // Check if admin is approved (only for admin routes)
  if (requireAdmin && (!isAdmin || !isApproved)) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Account Pending Approval</Typography>
          <Typography variant="body2">
            Your account is pending administrator approval. You will be notified via email once approved.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;